import { useEffect, useState, type FormEvent } from 'react';
import {
  approvePayrollFact,
  approvePayrollRun,
  createPayrollPeriod,
  createPayrollRun,
  generatePayrollLines,
  listPayrollFacts,
  listPayrollLines,
  listPayrollPeriods,
  listPayrollRuns,
  lockPayrollPeriod,
  postPayrollRunJournal,
  recordPayrollFact,
  listAccountingPeriods,
  listChartOfAccounts,
} from '../../api/finance';
import { listEmployees } from '../../api/hr';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
} from '../../components/common';
import type { AccountingPeriod, ChartOfAccount, Employee, PayrollFact, PayrollLine, PayrollPeriod, PayrollRun } from '../../types/domain';

const FACT_TYPE_LABEL: Record<PayrollFact['fact_type'], string> = {
  BASE_EARNING: 'Gaji Pokok',
  BONUS: 'Bonus',
  MANUAL_EARNING: 'Pendapatan Lain',
  LATE_DEDUCTION: 'Potongan Keterlambatan',
  LEAVE_UNPAID_DEDUCTION: 'Potongan Cuti Tanpa Gaji',
  MANUAL_DEDUCTION: 'Potongan Lain',
};

const currency = (n: number) => `Rp ${n.toLocaleString('id-ID')}`;

/**
 * Payroll workspace (FASE 10, migration 0044): Periode, Fakta Gaji,
 * Payroll Run in one workspace with tabs — mirrors the real backend's own
 * three-stage flow (period -> approved facts -> run -> lines -> journal).
 * Facts only ever reach a payroll_line once status='APPROVED'
 * (fn_generate_payroll_lines) — a DRAFT fact is never paid. No salary
 * structure exists in the canonical schema, so amounts are always human-
 * entered here, never auto-computed.
 */
export default function PayrollPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [tab, setTab] = useState<'periods' | 'facts' | 'runs'>('periods');
  const [periods, setPeriods] = useState<PayrollPeriod[]>([]);
  const [facts, setFacts] = useState<PayrollFact[]>([]);
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [accountingPeriods, setAccountingPeriods] = useState<AccountingPeriod[]>([]);
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canReadPeriod = hasPermission('payroll_period', 'read');
  const canReadFact = hasPermission('payroll_fact', 'read');
  const canReadRun = hasPermission('payroll_run', 'read');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [periodRows, factRows, runRows, empRows, apRows, coaRows] = await Promise.all([
        canReadPeriod ? listPayrollPeriods() : Promise.resolve([]),
        canReadFact ? listPayrollFacts() : Promise.resolve([]),
        canReadRun ? listPayrollRuns(activeBranchId ?? undefined) : Promise.resolve([]),
        listEmployees(activeBranchId ?? undefined),
        listAccountingPeriods(),
        listChartOfAccounts(),
      ]);
      setPeriods(periodRows);
      setFacts(factRows);
      setRuns(runRows);
      setEmployees(empRows);
      setAccountingPeriods(apRows);
      setAccounts(coaRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const employeeName = (id: string) => employees.find((e) => e.employee_id === id)?.legal_name ?? id;
  const periodCode = (id: string) => periods.find((p) => p.payroll_period_id === id)?.period_code ?? id;

  const tabs: Array<{ key: typeof tab; label: string }> = [
    { key: 'periods', label: 'Periode' },
    { key: 'facts', label: 'Fakta Gaji' },
    { key: 'runs', label: 'Payroll Run' },
  ];

  return (
    <div>
      <PageHeader title="Payroll" subtitle="Periode, fakta gaji (earning/deduction), dan payroll run. Migrasi 0044." />
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--alex-border)' }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              border: 'none',
              background: 'none',
              padding: '10px 14px',
              fontSize: 'var(--alex-fs-caption)',
              fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? 'var(--alex-primary)' : 'var(--alex-text-secondary)',
              borderBottom: tab === t.key ? '2px solid var(--alex-primary)' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && tab === 'periods' && (
        <PeriodsTab periods={periods} canManage={hasPermission('payroll_period', 'create')} canLock={hasPermission('payroll_period', 'lock')} onChanged={() => void load()} />
      )}
      {!loading && !error && tab === 'facts' && (
        <FactsTab
          facts={facts}
          periods={periods}
          employees={employees}
          employeeName={employeeName}
          periodCode={periodCode}
          canCreate={hasPermission('payroll_fact', 'create')}
          canApprove={hasPermission('payroll_fact', 'approve')}
          activeBranchId={activeBranchId}
          onChanged={() => void load()}
        />
      )}
      {!loading && !error && tab === 'runs' && (
        <RunsTab
          runs={runs}
          periods={periods}
          employeeName={employeeName}
          periodCode={periodCode}
          accountingPeriods={accountingPeriods}
          accounts={accounts}
          canCreate={hasPermission('payroll_run', 'create')}
          canApprove={hasPermission('payroll_run', 'approve')}
          canPost={hasPermission('payroll_run', 'post')}
          activeBranchId={activeBranchId}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function PeriodsTab({
  periods,
  canManage,
  canLock,
  onChanged,
}: {
  periods: PayrollPeriod[];
  canManage: boolean;
  canLock: boolean;
  onChanged: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [lockingId, setLockingId] = useState<string | null>(null);

  const handleLock = async (id: string) => {
    setActionErr(null);
    setLockingId(id);
    try {
      await lockPayrollPeriod(id);
      onChanged();
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLockingId(null);
    }
  };

  return (
    <div>
      {canManage && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }}>
          {showForm ? 'Batal' : '+ Periode baru'}
        </Button>
      )}
      {showForm && canManage && (
        <NewPeriodForm
          onCreated={() => {
            setShowForm(false);
            onChanged();
          }}
        />
      )}
      {actionErr && <ErrorNote text={actionErr} />}
      {periods.length === 0 && <EmptyState text="Belum ada periode payroll." />}
      {periods.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Kode</th>
              <th style={thStyle}>Mulai</th>
              <th style={thStyle}>Selesai</th>
              <th style={thStyle}>Status</th>
              {canLock && <th style={thStyle}></th>}
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => (
              <tr key={p.payroll_period_id}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{p.period_code}</td>
                <td style={tdStyle}>{p.start_date}</td>
                <td style={tdStyle}>{p.end_date}</td>
                <td style={tdStyle}>
                  <StatusBadge status={p.status} />
                </td>
                {canLock && (
                  <td style={tdStyle}>
                    {p.status === 'OPEN' && (
                      <Button variant="secondary" loading={lockingId === p.payroll_period_id} onClick={() => void handleLock(p.payroll_period_id)}>
                        Kunci
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NewPeriodForm({ onCreated }: { onCreated: () => void }) {
  const [periodCode, setPeriodCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createPayrollPeriod(periodCode.trim(), startDate, endDate);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 160 }}>
        <FieldLabel label="Kode Periode">
          <TextInput value={periodCode} onChange={(e) => setPeriodCode(e.target.value)} required autoFocus placeholder="2026-08-PR" />
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Mulai">
          <TextInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Selesai">
          <TextInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function FactsTab({
  facts,
  periods,
  employees,
  employeeName,
  periodCode,
  canCreate,
  canApprove,
  activeBranchId,
  onChanged,
}: {
  facts: PayrollFact[];
  periods: PayrollPeriod[];
  employees: Employee[];
  employeeName: (id: string) => string;
  periodCode: (id: string) => string;
  canCreate: boolean;
  canApprove: boolean;
  activeBranchId: string | null;
  onChanged: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const draftCount = facts.filter((f) => f.status === 'DRAFT').length;

  const handleApprove = async (id: string) => {
    setActionErr(null);
    setApprovingId(id);
    try {
      await approvePayrollFact(id);
      onChanged();
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : String(e));
    } finally {
      setApprovingId(null);
    }
  };

  return (
    <div>
      <SummaryStrip
        items={[
          { label: 'Total Fakta', value: facts.length },
          { label: 'Draft', value: draftCount, tone: draftCount > 0 ? ('warning' as const) : ('neutral' as const) },
          { label: 'Disetujui', value: facts.length - draftCount, tone: 'success' as const },
        ]}
      />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Catat Fakta Gaji'}
        </Button>
      )}
      {showForm && canCreate && activeBranchId && (
        <NewFactForm
          branchId={activeBranchId}
          periods={periods}
          employees={employees}
          onCreated={() => {
            setShowForm(false);
            onChanged();
          }}
        />
      )}
      {actionErr && <ErrorNote text={actionErr} />}
      {facts.length === 0 && <EmptyState text="Belum ada fakta gaji tercatat." />}
      {facts.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Karyawan</th>
              <th style={thStyle}>Periode</th>
              <th style={thStyle}>Jenis</th>
              <th style={thStyle}>Sumber</th>
              <th style={thStyle}>Jumlah</th>
              <th style={thStyle}>Status</th>
              {canApprove && <th style={thStyle}></th>}
            </tr>
          </thead>
          <tbody>
            {facts.map((f) => (
              <tr key={f.fact_id}>
                <td style={tdStyle}>{employeeName(f.employee_id)}</td>
                <td style={tdStyle}>{periodCode(f.payroll_period_id)}</td>
                <td style={tdStyle}>{FACT_TYPE_LABEL[f.fact_type]}</td>
                <td style={tdStyle}>{f.source_type === 'MANUAL' ? 'Manual' : f.source_type}</td>
                <td style={tdStyle}>{currency(f.amount)}</td>
                <td style={tdStyle}>
                  <StatusBadge status={f.status} />
                </td>
                {canApprove && (
                  <td style={tdStyle}>
                    {f.status === 'DRAFT' && (
                      <Button variant="secondary" loading={approvingId === f.fact_id} onClick={() => void handleApprove(f.fact_id)}>
                        Setujui
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NewFactForm({
  branchId,
  periods,
  employees,
  onCreated,
}: {
  branchId: string;
  periods: PayrollPeriod[];
  employees: Employee[];
  onCreated: () => void;
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [payrollPeriodId, setPayrollPeriodId] = useState('');
  const [factType, setFactType] = useState<PayrollFact['fact_type']>('BASE_EARNING');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openPeriods = periods.filter((p) => p.status === 'OPEN');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Jumlah harus lebih dari 0.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await recordPayrollFact(branchId, employeeId, payrollPeriodId, factType, 'MANUAL', null, amt, note || null);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Karyawan">
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {employees.map((emp) => (
              <option key={emp.employee_id} value={emp.employee_id}>
                {emp.employee_no} — {emp.legal_name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 180 }}>
        <FieldLabel label="Periode (OPEN)">
          <Select value={payrollPeriodId} onChange={(e) => setPayrollPeriodId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {openPeriods.map((p) => (
              <option key={p.payroll_period_id} value={p.payroll_period_id}>
                {p.period_code}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 190 }}>
        <FieldLabel label="Jenis">
          <Select value={factType} onChange={(e) => setFactType(e.target.value as PayrollFact['fact_type'])}>
            {Object.entries(FACT_TYPE_LABEL).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Jumlah (Rp)">
          <TextInput type="number" min="0" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <FieldLabel label="Catatan (opsional)">
          <TextInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="mis. mengacu ke keterlambatan tanggal ..." />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Catat
      </Button>
      <div style={{ width: '100%', fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)' }}>
        Jumlah selalu diinput manual — tidak ada struktur gaji otomatis di skema saat ini.
      </div>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function RunsTab({
  runs,
  periods,
  employeeName,
  periodCode,
  accountingPeriods,
  accounts,
  canCreate,
  canApprove,
  canPost,
  activeBranchId,
  onChanged,
}: {
  runs: PayrollRun[];
  periods: PayrollPeriod[];
  employeeName: (id: string) => string;
  periodCode: (id: string) => string;
  accountingPeriods: AccountingPeriod[];
  accounts: ChartOfAccount[];
  canCreate: boolean;
  canApprove: boolean;
  canPost: boolean;
  activeBranchId: string | null;
  onChanged: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState<PayrollRun | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const openPeriods = periods.filter((p) => p.status === 'OPEN');

  const handleGenerate = async (id: string) => {
    setActionErr(null);
    setBusyId(id);
    try {
      await generatePayrollLines(id);
      onChanged();
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleApprove = async (id: string) => {
    setActionErr(null);
    setBusyId(id);
    try {
      await approvePayrollRun(id);
      onChanged();
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Payroll Run baru'}
        </Button>
      )}
      {showForm && canCreate && activeBranchId && (
        <NewRunForm
          branchId={activeBranchId}
          periods={openPeriods}
          onCreated={() => {
            setShowForm(false);
            onChanged();
          }}
        />
      )}
      {actionErr && <ErrorNote text={actionErr} />}
      {runs.length === 0 && <EmptyState text="Belum ada payroll run." />}
      {runs.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>No. Run</th>
              <th style={thStyle}>Periode</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <ClickableRow key={r.payroll_run_id} onClick={() => setSelected(r)}>
                <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{r.run_no}</td>
                <td style={tdStyle}>{periodCode(r.payroll_period_id)}</td>
                <td style={tdStyle}>
                  <StatusBadge status={r.status} />
                </td>
                <td style={tdStyle}>
                  {r.status === 'DRAFT' && canCreate && (
                    <Button
                      variant="secondary"
                      loading={busyId === r.payroll_run_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleGenerate(r.payroll_run_id);
                      }}
                    >
                      Generate Baris
                    </Button>
                  )}
                  {r.status === 'DRAFT' && canApprove && (
                    <Button
                      variant="primary"
                      loading={busyId === r.payroll_run_id}
                      style={{ marginLeft: 6 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleApprove(r.payroll_run_id);
                      }}
                    >
                      Setujui
                    </Button>
                  )}
                </td>
              </ClickableRow>
            ))}
          </tbody>
        </table>
      )}
      <RunDetailDrawer
        run={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        employeeName={employeeName}
        accountingPeriods={accountingPeriods}
        accounts={accounts}
        canPost={canPost}
        onChanged={onChanged}
      />
    </div>
  );
}

function NewRunForm({ branchId, periods, onCreated }: { branchId: string; periods: PayrollPeriod[]; onCreated: () => void }) {
  const [payrollPeriodId, setPayrollPeriodId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createPayrollRun(branchId, payrollPeriodId);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 220 }}>
        <FieldLabel label="Periode (OPEN)">
          <Select value={payrollPeriodId} onChange={(e) => setPayrollPeriodId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {periods.map((p) => (
              <option key={p.payroll_period_id} value={p.payroll_period_id}>
                {p.period_code}
              </option>
            ))}
          </Select>
        </FieldLabel>
        {periods.length === 0 && <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 4 }}>Tidak ada periode OPEN.</div>}
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Buat Run
      </Button>
      <div style={{ width: '100%', fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)' }}>
        Satu run per cabang per periode — run duplikat akan ditolak server.
      </div>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function RunDetailDrawer({
  run,
  open,
  onClose,
  employeeName,
  accountingPeriods,
  accounts,
  canPost,
  onChanged,
}: {
  run: PayrollRun | null;
  open: boolean;
  onClose: () => void;
  employeeName: (id: string) => string;
  accountingPeriods: AccountingPeriod[];
  accounts: ChartOfAccount[];
  canPost: boolean;
  onChanged: () => void;
}) {
  const [lines, setLines] = useState<PayrollLine[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [accountingPeriodId, setAccountingPeriodId] = useState('');
  const [salaryExpenseAccountId, setSalaryExpenseAccountId] = useState('');
  const [deductionClearingAccountId, setDeductionClearingAccountId] = useState('');
  const [payrollPayableAccountId, setPayrollPayableAccountId] = useState('');
  const [posting, setPosting] = useState(false);
  const [postErr, setPostErr] = useState<string | null>(null);

  useEffect(() => {
    setLines(null);
    setLoadErr(null);
    setPostErr(null);
    if (!run) return;
    listPayrollLines(run.payroll_run_id).then(setLines).catch((e) => setLoadErr(e instanceof Error ? e.message : String(e)));
  }, [run?.payroll_run_id]);

  if (!run) return null;

  const postableAccounts = accounts.filter((a) => a.is_postable && a.status === 'ACTIVE');
  const openAccountingPeriods = accountingPeriods.filter((p) => p.status === 'OPEN');

  const handlePost = async () => {
    setPosting(true);
    setPostErr(null);
    try {
      await postPayrollRunJournal(run.payroll_run_id, accountingPeriodId, new Date().toISOString().slice(0, 10), salaryExpenseAccountId, deductionClearingAccountId, payrollPayableAccountId);
      onChanged();
      onClose();
    } catch (err) {
      setPostErr(err instanceof Error ? err.message : String(err));
    } finally {
      setPosting(false);
    }
  };

  const totalGross = lines?.reduce((s, l) => s + l.gross_amount, 0) ?? 0;
  const totalDeduction = lines?.reduce((s, l) => s + l.deduction_amount, 0) ?? 0;
  const totalBonus = lines?.reduce((s, l) => s + l.bonus_amount, 0) ?? 0;
  const totalNet = lines?.reduce((s, l) => s + l.net_amount, 0) ?? 0;

  return (
    <Drawer open={open} onClose={onClose} title={`Payroll Run ${run.run_no}`}>
      <DrawerField label="Status" value={<StatusBadge status={run.status} />} />
      <DrawerField label="Dibuat" value={formatDateTime(run.created_at)} />
      {run.approved_at && <DrawerField label="Disetujui" value={formatDateTime(run.approved_at)} />}

      <div style={{ marginTop: 14, fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-primary)', marginBottom: 8 }}>Rincian per Karyawan (Payslip)</div>
      {loadErr && <ErrorNote text={loadErr} />}
      {lines === null && <LoadingNote />}
      {lines && lines.length === 0 && <EmptyState text="Belum ada baris — generate dari fakta gaji APPROVED terlebih dahulu." variant="compact" />}
      {lines && lines.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Karyawan</th>
              <th style={thStyle}>Bruto</th>
              <th style={thStyle}>Potongan</th>
              <th style={thStyle}>Bonus</th>
              <th style={thStyle}>Netto</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.payroll_line_id}>
                <td style={tdStyle}>{employeeName(l.employee_id)}</td>
                <td style={tdStyle}>{currency(l.gross_amount)}</td>
                <td style={tdStyle}>{currency(l.deduction_amount)}</td>
                <td style={tdStyle}>{currency(l.bonus_amount)}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(l.net_amount)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...tdStyle, fontWeight: 700 }}>Total</td>
              <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(totalGross)}</td>
              <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(totalDeduction)}</td>
              <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(totalBonus)}</td>
              <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(totalNet)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {run.status === 'APPROVED' && canPost && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--alex-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--alex-fs-caption)', marginBottom: 8 }}>Posting ke Jurnal</div>
          <FieldLabel label="Periode Akuntansi (OPEN)">
            <Select value={accountingPeriodId} onChange={(e) => setAccountingPeriodId(e.target.value)}>
              <option value="">Pilih...</option>
              {openAccountingPeriods.map((p) => (
                <option key={p.period_id} value={p.period_id}>
                  {p.period_code}
                </option>
              ))}
            </Select>
          </FieldLabel>
          <div style={{ marginTop: 8 }}>
            <FieldLabel label="Akun Beban Gaji (Debit)">
              <Select value={salaryExpenseAccountId} onChange={(e) => setSalaryExpenseAccountId(e.target.value)}>
                <option value="">Pilih...</option>
                {postableAccounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.account_code} — {a.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ marginTop: 8 }}>
            <FieldLabel label="Akun Kliring Potongan (Kredit, jika ada potongan)">
              <Select value={deductionClearingAccountId} onChange={(e) => setDeductionClearingAccountId(e.target.value)}>
                <option value="">Pilih...</option>
                {postableAccounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.account_code} — {a.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ marginTop: 8 }}>
            <FieldLabel label="Akun Utang Gaji (Kredit)">
              <Select value={payrollPayableAccountId} onChange={(e) => setPayrollPayableAccountId(e.target.value)}>
                <option value="">Pilih...</option>
                {postableAccounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.account_code} — {a.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <Button
            variant="primary"
            loading={posting}
            style={{ marginTop: 12 }}
            onClick={() => void handlePost()}
            disabled={!accountingPeriodId || !salaryExpenseAccountId || !payrollPayableAccountId}
          >
            Posting Jurnal
          </Button>
          {postErr && <ErrorNote text={postErr} />}
        </div>
      )}
    </Drawer>
  );
}
